import { type FormEvent, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type {
  ScheduleEntry,
  ScheduleOverride,
  UpsertScheduleOverrideInput,
} from "../../lib/realestate";
import { Modal } from "../ui/Modal";

interface ScheduleOverrideFormModalProps {
  open: boolean;
  onClose: () => void;
  initial: ScheduleOverride | null;
  defaultMonth?: string; // YYYY-MM
  scheduleEntry?: ScheduleEntry; // fuer Placeholder-Werte
  onSubmit: (input: UpsertScheduleOverrideInput) => Promise<unknown>;
  submitting?: boolean;
}

function formatEur(cents: number): string {
  return (cents / 100).toLocaleString("de-DE", { style: "currency", currency: "EUR" });
}

function toMonthInput(iso: string | undefined): string {
  if (!iso) return "";
  return iso.slice(0, 7); // YYYY-MM
}

function monthInputToIsoDate(m: string): string {
  return `${m}-01`;
}

export function ScheduleOverrideFormModal({
  open,
  onClose,
  initial,
  defaultMonth,
  scheduleEntry,
  onSubmit,
  submitting,
}: ScheduleOverrideFormModalProps) {
  const { t } = useTranslation();

  const initialMonth = useMemo(
    () => toMonthInput(initial?.month) || defaultMonth || "",
    [initial, defaultMonth],
  );

  const [month, setMonth] = useState(initialMonth);
  const [interestEur, setInterestEur] = useState<string>(
    initial?.interestCents != null ? (initial.interestCents / 100).toFixed(2) : "",
  );
  const [principalEur, setPrincipalEur] = useState<string>(
    initial?.principalCents != null ? (initial.principalCents / 100).toFixed(2) : "",
  );
  const [notes, setNotes] = useState(initial?.notes ?? "");

  useEffect(() => {
    setMonth(initialMonth);
    setInterestEur(initial?.interestCents != null ? (initial.interestCents / 100).toFixed(2) : "");
    setPrincipalEur(
      initial?.principalCents != null ? (initial.principalCents / 100).toFixed(2) : "",
    );
    setNotes(initial?.notes ?? "");
  }, [initial, initialMonth]);

  const interestCents =
    interestEur.trim() === "" ? null : Math.round(Number.parseFloat(interestEur) * 100);
  const principalCents =
    principalEur.trim() === "" ? null : Math.round(Number.parseFloat(principalEur) * 100);
  const empty = interestCents === null && principalCents === null;
  const saveDisabled = empty || !month;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (saveDisabled) return;
    await onSubmit({
      month: monthInputToIsoDate(month),
      interestCents,
      principalCents,
      notes: notes.trim() === "" ? null : notes,
    });
  }

  function handlePauseQuickAction() {
    setPrincipalEur("0");
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t(initial ? "realestate.override.edit" : "realestate.override.add")}
    >
      <form onSubmit={handleSubmit} className="space-y-3">
        <label className="block">
          <span className="text-sm">{t("realestate.override.month")}</span>
          <input
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className="block w-full border rounded px-2 py-1"
          />
        </label>

        <label className="block">
          <span className="text-sm">{t("realestate.override.interest")}</span>
          <input
            type="number"
            step="0.01"
            min="0"
            value={interestEur}
            onChange={(e) => setInterestEur(e.target.value)}
            placeholder={
              scheduleEntry
                ? t("realestate.override.interest_hint", {
                    value: formatEur(scheduleEntry.interestCents),
                  })
                : ""
            }
            className="block w-full border rounded px-2 py-1 font-mono"
          />
        </label>

        <label className="block">
          <span className="text-sm">{t("realestate.override.principal")}</span>
          <input
            type="number"
            step="0.01"
            min="0"
            value={principalEur}
            onChange={(e) => setPrincipalEur(e.target.value)}
            placeholder={
              scheduleEntry
                ? t("realestate.override.principal_hint", {
                    value: formatEur(scheduleEntry.principalCents),
                  })
                : ""
            }
            className="block w-full border rounded px-2 py-1 font-mono"
          />
        </label>

        <button
          type="button"
          onClick={handlePauseQuickAction}
          className="text-xs underline text-blue-600"
        >
          {t("realestate.override.pause_quick_action")}
        </button>

        <label className="block">
          <span className="text-sm">{t("realestate.override.notes")}</span>
          <textarea
            value={notes}
            maxLength={500}
            onChange={(e) => setNotes(e.target.value)}
            className="block w-full border rounded px-2 py-1"
            rows={2}
          />
        </label>

        {empty && (
          <p className="text-xs text-amber-700">{t("realestate.override.empty_warning")}</p>
        )}

        <div className="flex justify-end gap-2">
          <button type="button" className="px-3 py-1 border rounded" onClick={onClose}>
            {t("common.cancel")}
          </button>
          <button
            type="submit"
            disabled={saveDisabled || submitting}
            className="px-3 py-1 bg-blue-600 text-white rounded disabled:opacity-50"
          >
            {t("common.save")}
          </button>
        </div>
      </form>
    </Modal>
  );
}
